import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Wrapper } from './style';
import { Collapse, message, Select, Carousel, Spin, Tooltip } from 'antd';
import { CaretLeftFilled, CaretRightFilled, InfoCircleOutlined } from '@ant-design/icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { ConvertParams } from '../../../../config';
import { MakeApiCall } from '../../../../apis';
import ItemMaster from '../item-list';
import { getAmount } from '../../../../utils/helper';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    LineController,
    BarController,
    Title,
    ChartTooltip,
    Legend
);

const { Option } = Select;

const AMOUNT_PARAMS = new Set([
    'sales','promo','total_tax','additional_charges','total_fba_fees',
    'amazon_fees','refund_cost','removal_shipment','ppc_costs','promotions',
    'adjustments','adhoc_disbursement','estimated_payout','debt_payment',
    'total_cogs','total_expenses','gross_profit','net_profit'
]);
const UNITS_PARAMS = new Set(['units_sold', 'total_refunds', 'total_sessions', 'orders', 'total_orders']);
const RATIO_PARAMS = new Set(['profit_margin', 'total_roi']);

const COLORS = [
    '#4285F4','#DB4437','#F4B400','#0F9D58','#AB47BC','#00ACC1','#FF7043','#9E9D24',
    '#5C6BC0','#00796B','#C2185B','#7B1FA2','#F06292','#4DB6AC','#D4E157','#FF8F00',
    '#8D6E63','#F4511E','#3949AB','#1E88E5','#43A047','#FDD835','#6D4C41','#2E7D32'
];

// Formats date strings for the X-axis and tooltip:
//   "2026-02-06" → "06 Feb"
//   "2026-02"    → "Feb 2026"
//   Anything else is returned as-is (safe fallback)
const formatDateLabel = (dateStr) => {
    if (!dateStr) return dateStr;
    // Month-only: "YYYY-MM" → "Feb 2026"
    if (/^\d{4}-\d{2}$/.test(dateStr)) {
        const [year, month] = dateStr.split('-');
        const date = new Date(Number(year), Number(month) - 1, 1);
        return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    }
    // Full date: "YYYY-MM-DD" → "06 Feb"
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    return dateStr;
};

const ChartComponent = ({
                            userData,
                            selectedMarketplace,
                            selectedCurrencySign,
                            periodicity,
                            otherRange,
                            selectedSku
                        }) => {
    const isInitialLoad = useRef(true);
    const fetchedFilterKeyRef = useRef("");

    const [filters, setFilters] = useState({
        page: 1,
        'per-page': 20,
        report_type: periodicity,
        start_date: otherRange.start_date,
        end_date: otherRange.end_date,
        sku: selectedSku?.join(',')
    });

    const [loading, setLoading] = useState(true); // Handles Summary Data Loading
    const [tilesList, setTilesList] = useState([]);
    const [chartLoading, setChartLoading] = useState(false); // Handles Chart Data Loading
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [selectedParameterKeys, setSelectedParameterKeys] = useState([]);
    const [defaultPresets, setDefaultPresets] = useState({});
    const [parameterOptions, setParameterOptions] = useState([]);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [seriesDefinitions, setSeriesDefinitions] = useState([]);

    const [rawChartRows, setRawChartRows] = useState([]);

    const buildFilterKey = (f) => JSON.stringify({
        marketplace_id: f.marketplace_id,
        report_type: f.report_type,
        start_date: f.start_date,
        end_date: f.end_date,
        sku: f.sku
    });

    useEffect(() => {
        if (!selectedMarketplace?.id) return;
        const nextFilters = {
            page: 1,
            'per-page': 20,
            marketplace_id: selectedMarketplace.id,
            report_type: periodicity,
            start_date: otherRange.start_date,
            end_date: otherRange.end_date,
            sku: selectedSku?.join(',')
        };

        setFilters(prev => {
            if (JSON.stringify(prev) === JSON.stringify(nextFilters)) return prev;
            return nextFilters;
        });
    }, [selectedMarketplace, periodicity, otherRange, selectedSku]);

    useEffect(() => { fetchFilterData(); }, []);

    // Effect: Independent triggers for non-blocking UI
    useEffect(() => {
        if (!filters.marketplace_id) return;

        const currentKey = buildFilterKey(filters);
        if (fetchedFilterKeyRef.current === currentKey) return;

        fetchedFilterKeyRef.current = currentKey;

        // Call both APIs separately
        fetchProfitAndLossData();
        fetchAllChartData();
    }, [filters]);

    useEffect(() => {
        if (rawChartRows.length > 0) {
            updateChartUI(selectedParameterKeys, rawChartRows);
        } else if (selectedParameterKeys.length === 0) {
            setChartData({ labels: [], datasets: [] });
        }
    }, [selectedParameterKeys, rawChartRows]);

    const fetchProfitAndLossData = async () => {
        setLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/chart/get-summary-data${ConvertParams(filters)}`,
                'get', null, true, {}, userData?.token
            );
            if (response?.status) setTilesList(response?.data?.summaryData || []);
            else message.warning(response?.message);
        } catch (e) {
            message.error('Error fetching summary data.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllChartData = async () => {
        setChartLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/chart/get-chart-data${ConvertParams(filters)}`,
                'get', null, true, {}, userData?.token
            );
            if (response?.status) {
                const rows = filters.report_type === 'month' ? (response.data || []).slice() : (response.data || []).slice().reverse();
                setRawChartRows(rows);
            } else {
                setRawChartRows([]);
                setChartData({ labels: [], datasets: [] });
            }
        } catch (e) {
            message.error('Error fetching chart data.');
            setRawChartRows([]);
        } finally {
            setChartLoading(false);
        }
    };

    const fetchFilterData = async () => {
        try {
            const response = await MakeApiCall('dashboard/chart/get-parameters', 'get', null, true, {}, userData?.token);
            if (!response?.status) return;
            const { default_presets, all_parameters } = response.data;
            setDefaultPresets(default_presets);
            const formatted = Object.entries(all_parameters)
                .filter(([value]) => value !== 'total_sessions')
                .map(([value, label]) => ({ value, label }));
            setParameterOptions(formatted);

            if (isInitialLoad.current) {
                const profitHealthKey = Object.keys(default_presets).find(k => default_presets[k]?.label === 'Profit Health');
                if (profitHealthKey) {
                    setSelectedPreset(profitHealthKey);
                    setSelectedParameterKeys(Object.keys(default_presets[profitHealthKey]?.parameters));
                }
                isInitialLoad.current = false;
            }
        } catch (e) {
            message.error('Error fetching filter configuration.');
        }
    };

    const getValueForParam = useCallback((row, paramKey) => {
        const normalized = String(paramKey).toLowerCase();
        if (normalized === 'orders' || normalized === 'total_orders') return row.total_orders || 0;
        return (row[paramKey] !== undefined && row[paramKey] !== null) ? row[paramKey] : 0;
    }, []);

    const formatLabel = useCallback((paramKey) => {
        const opt = parameterOptions.find(o => String(o.value) === String(paramKey));
        if (opt?.label) return opt.label;
        return String(paramKey).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }, [parameterOptions]);

    const buildDatasets = useCallback((params, dataRows) => {
        const seriesDefs = [];
        const datasets = params.map((param, idx) => {
            const isUnit = UNITS_PARAMS.has(param);
            const isRatio = RATIO_PARAMS.has(param);
            const isAmount = AMOUNT_PARAMS.has(param);
            const type = (isUnit || isRatio) ? 'bar' : 'line';
            const yAxisID = (isUnit || isRatio) ? 'y1' : 'y';
            const color = COLORS[idx % COLORS.length];

            seriesDefs.push({ paramKey: param, isUnit, isRatio, isAmount });

            return {
                label: formatLabel(param),
                data: dataRows.map(row => getValueForParam(row, param)),
                type,
                yAxisID,
                borderColor: type === 'line' ? color : undefined,
                backgroundColor: type === 'bar' ? color : 'transparent',
                tension: 0.3,
                pointBackgroundColor: color,
                order: type === 'line' ? 1 : 2
            };
        });
        return { seriesDefs, datasets };
    }, [formatLabel, getValueForParam]);

    const updateChartUI = (params, rows) => {
        const labels = rows.map(r => r.report_date);
        const { seriesDefs, datasets } = buildDatasets(params, rows);
        setSeriesDefinitions(seriesDefs);
        setChartData({ labels, datasets });
    };

    const handleParameterChange = (values) => setSelectedParameterKeys(values);

    const handlePresetChange = (presetKey) => {
        setSelectedPreset(presetKey);
        const presetParams = defaultPresets[presetKey]?.parameters || {};
        setSelectedParameterKeys(Object.keys(presetParams).filter(k => k !== 'total_sessions'));
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    title: (items) => formatDateLabel(chartData.labels[items[0]?.dataIndex]),
                    label: (ctx) => {
                        let label = ctx.dataset.label + ': ';
                        const val = ctx.parsed.y;
                        const series = seriesDefinitions[ctx.datasetIndex];
                        if (series?.isUnit) return label + val.toFixed(0) + ' Units';
                        if (series?.isRatio) return label + val.toFixed(2) + '%';
                        if (series?.isAmount) return label + getAmount(val, selectedCurrencySign);
                        return label + val;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'category',
                ticks: {
                    callback: function(value, index) {
                        return formatDateLabel(chartData.labels[index]);
                    },
                    maxRotation: 90,
                    minRotation: 0,
                    autoSkip: true,
                }
            },
            y: { type: 'linear', position: 'left', ticks: { callback: (v) => getAmount(v, selectedCurrencySign) } },
            y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Units / ROI / Margin' } }
        }
    };

    const CustomPrevArrow = (props) => <CaretLeftFilled className={props.className} style={{ fontSize: '40px', color: '#181C31', zIndex: 1, left: '-10px' }} onClick={props.onClick} />;
    const CustomNextArrow = (props) => <CaretRightFilled className={props.className} style={{ fontSize: '40px', color: '#181C31', zIndex: 1, right: '-10px' }} onClick={props.onClick} />;

    const generateCollapseItems = (summaryData) => {
        if (!summaryData || Object.keys(summaryData).length === 0)
            return { performanceMetrics: [], profitLossMetrics: [], revenueMetrics: [], costMetrics: [] };

        const latestData = summaryData;
        const sections = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(latestData.amazon_fees?.total || 0, selectedCurrencySign),
                items: Object.entries(latestData.amazon_fees || {})
                    .filter(([k]) => k !== 'total')
                    .map(([k, v]) => ({
                        label: k
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())
                            .replace(/\bFba\b/g, 'FBA'),
                        value: getAmount(v, selectedCurrencySign)
                    }))
            },
            {
                key: '2',
                title: 'Advertising cost',
                amount: getAmount(-(latestData.advertising_cost?.total || 0), selectedCurrencySign),
                items: [
                    { label: 'Sponsored Product', value: getAmount(-(latestData.advertising_cost?.sp_cost || 0), selectedCurrencySign) },
                    { label: 'Sponsored Brands', value: getAmount(-(latestData.advertising_cost?.sb_cost || 0), selectedCurrencySign) },
                    { label: 'Sponsored Display', value: getAmount(-(latestData.advertising_cost?.sd_cost || 0), selectedCurrencySign) },
                    { label: 'Other advertising expenses', value: getAmount(-(latestData.advertising_cost?.external_advertising_expense || 0), selectedCurrencySign) }
                ]
            },
            {
                key: '3',
                title: 'Cost of Goods',
                amount: getAmount(-(latestData.cost_of_goods?.total || 0), selectedCurrencySign),
                items: [{ label: 'COGS', value: getAmount(-(latestData.cost_of_goods?.cost_of_goods_sold || 0), selectedCurrencySign) }]
            },
            {
                key: '4',
                title: 'Expenses',
                amount: getAmount(-(latestData.expenses?.total || 0), selectedCurrencySign),
                items: Object.entries(latestData.expenses || {}).filter(([k]) => k !== 'total').map(([k, v]) => ({
                    label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                    value: getAmount(-v, selectedCurrencySign)
                }))
            },
            {
                key: '5',
                title: 'Net Tax',
                amount: getAmount(latestData.tax_liability?.total || 0, selectedCurrencySign),
                items: [
                    {
                        label: 'Tax Collected',
                        value: getAmount(latestData.tax_liability?.total_tax_collected || 0, selectedCurrencySign)
                    },
                    {
                        label: (
                            <div className="d-flex align-items-center">
                                Tax Paid
                                {latestData.tax_liability?.tax_paid_breakdown && (
                                    <Tooltip
                                        placement="top"
                                        title={
                                            <div className="p-1">
                                                <div className="d-flex justify-content-between mb-1" style={{ minWidth: '160px' }}>
                                                    <span>Tax paid on COGS:</span>
                                                    <span className="fw-bold ms-4">{getAmount(latestData.tax_liability.tax_paid_breakdown.cogs, selectedCurrencySign)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span>Tax paid on Amazon fee:</span>
                                                    <span className="fw-bold ms-4">{getAmount(latestData.tax_liability.tax_paid_breakdown.fees, selectedCurrencySign)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>Tax paid on Ads:</span>
                                                    <span className="fw-bold ms-4">{getAmount(latestData.tax_liability.tax_paid_breakdown.advertising, selectedCurrencySign)}</span>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                            </div>
                        ),
                        value: getAmount(latestData.tax_liability?.total_tax_paid || 0, selectedCurrencySign)
                    }
                ]
            },
            { key: '6', title: 'Gross Profit', amount: getAmount(latestData.gross_profit || 0, selectedCurrencySign), items: [] },
            {
                key: '7',
                title: (
                    <div className="d-flex align-items-center">
                        Net Profit
                        <Tooltip title="Formula: Net Profit = Gross Profit - Expense + Net Tax" placement="top">
                            <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                        </Tooltip>
                    </div>
                ),
                amount: getAmount(latestData.net_profit || 0, selectedCurrencySign),
                items: []
            },
            { key: '8', title: 'Estimated Payouts', amount: getAmount(latestData.est_payout || 0, selectedCurrencySign), items: [] },
            { key: '9', title: 'Payments', amount: getAmount(latestData.payment_transfer?.total || 0, selectedCurrencySign), items: [] },
            {
                key: '10',
                title: 'Total Sales',
                amount: getAmount(latestData.sales?.total || 0, selectedCurrencySign),
                items: [
                    { label: 'Organic', value: getAmount(latestData.sales?.organic || 0, selectedCurrencySign) },
                    { label: 'Sponsored Product', value: getAmount(latestData.sales?.sp_sales || 0, selectedCurrencySign) },
                    { label: 'Sponsored Brand', value: getAmount(latestData.sales?.sb_sales || 0, selectedCurrencySign) },
                    { label: 'Sponsored Display', value: getAmount(latestData.sales?.sd_sales || 0, selectedCurrencySign) }
                ]
            },
            {
                key: '11',
                title: 'Advertising clicks',
                amount: `${latestData.advertising_clicks?.total || 0}`,
                items: [
                    { label: 'Sponsored Product', value: latestData.advertising_clicks?.sp_click || 0 },
                    { label: 'Sponsored Brand', value: latestData.advertising_clicks?.sb_click || 0 },
                    { label: 'Sponsored Display', value: latestData.advertising_clicks?.sd_click || 0 }
                ]
            },
            {
                key: '12',
                title: 'Total units',
                amount: `${latestData.units?.total || 0}`,
                items: [
                    { label: 'Organic', value: latestData.units?.organic || 0 },
                    { label: 'Sponsored Product', value: latestData.units?.sp_units || 0 },
                    { label: 'Sponsored Brand', value: latestData.units?.sb_units || 0 },
                    { label: 'Sponsored Display', value: latestData.units?.sd_units || 0 }
                ]
            },
            { key: '13', title: 'Total orders', amount: `${latestData.total_orders || 0}`, items: [] },
            {
                key: '14',
                title: (
                    <div className="d-flex align-items-center">
                        Ad conversion
                        <Tooltip title="Formula: (SP Units + SB Units + SD Units) / Total Ad Clicks * 100" placement="top">
                            <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '14px', cursor: 'help' }} />
                        </Tooltip>
                    </div>
                ),
                amount: `${(latestData.advertising_clicks?.total > 0) ? (((latestData.units.sp_units + latestData.units.sb_units + latestData.units.sd_units) / latestData.advertising_clicks.total) * 100).toFixed(2) : "0"}%`,
                items: []
            },
            { key: '15', title: 'Refunds %', amount: `${(latestData.refunds_percent || 0).toFixed(2)}%`, items: [] }
        ];

        const formatSections = (items) => items.map((section) => ({
            key: section.key,
            label: (
                <div className="d-flex justify-content-between w-100">
                    <span className="text-gray-800 fw-bold fs-6">{section.title}</span>
                    <span className="text-gray-800 fw-bolder fs-6 me-3 gotham_black">{section.amount}</span>
                </div>
            ),
            children: section.items.length > 0 ? (
                <>
                    {section.items.map((item, i) => (
                        <React.Fragment key={i}>
                            <div className="row g-4 mt-0 ps-9">
                                <div className="col w-md-100 d-flex align-items-center justify-content-between pe-md-0 mt-1">
                                    <h3 className="mb-1 fw-semibold text-gray-700 fs-6">{item.label}</h3>
                                    <h3 className="fs-6 text-gray-800 fw-bolder mb-0">{item.value}</h3>
                                </div>
                            </div>
                            {i < section.items.length - 1 && <div className="separator separator-dashed border-gray-300 my-2" />}
                        </React.Fragment>
                    ))}
                </>
            ) : null,
            showArrow: section.items.length > 0,
            collapsible: section.items.length > 0 ? '' : 'disabled',
            className: section.items.length === 0 ? 'ps-9' : ''
        }));

        return {
            costMetrics: formatSections(sections.slice(0, 5)),
            profitLossMetrics: formatSections(sections.slice(5, 9)),
            performanceMetrics: formatSections(sections.slice(9, 15))
        };
    };

    const { performanceMetrics, profitLossMetrics, costMetrics } = generateCollapseItems(tilesList);

    return (
        <Wrapper>
            <div className="row g-5 g-xl-5 dashboard-cus-row">
                <div className="col-xxl-8 mb-5 mb-xl-5 d-none d-md-block">
                    <div className="card h-100">
                        <div className="card-header">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bold text-dark">Overview</span>
                            </h3>
                            <div className="card-toolbar">
                                <div className="d-flex responsive-select-container">
                                    <Select
                                        placeholder="Select Preset"
                                        className="me-2 custom-select-width"
                                        size="large"
                                        value={selectedPreset}
                                        onChange={handlePresetChange}
                                    >
                                        {Object.entries(defaultPresets).map(([key, preset]) => (
                                            <Option key={key} value={key}>{preset.label}</Option>
                                        ))}
                                    </Select>
                                    <Select
                                        mode="multiple"
                                        size="large"
                                        className="custom-select-width"
                                        placeholder="Select Parameters"
                                        value={selectedParameterKeys}
                                        onChange={handleParameterChange}
                                        options={parameterOptions}
                                        maxTagCount={0}
                                        maxTagPlaceholder={() => selectedParameterKeys.length > 0 ? `${selectedParameterKeys.length} selected` : 'No parameter selected'}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-body pb-5 d-flex flex-column">
                            {/* Chart-specific Loading Indicator */}
                            {chartLoading ? (
                                <div className="d-flex justify-content-center align-items-center flex-grow-1"
                                     style={{minHeight: '400px'}}>
                                    <Spin size="large" tip="Loading Chart..."/>
                                </div>
                            ) : (
                                <div className="flex-grow-1" style={{minHeight: '400px'}}>
                                    <Chart type="bar" options={chartOptions} data={chartData}/>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xxl-4 mb-md-5 mb-xl-5 d-md-block">
                    <div className="card card-flush h-md-100">
                        <div className="card-header p-3 px-5" style={{display: 'flex', flexDirection: 'column'}}>
                            <div className="border-till border rounded p-4 px-5 mb-3"
                                 style={{borderColor: '#63CE94 !important', background: '#d1e3f6'}}>
                                <div className="d-flex align-items-center justify-content-center">
                                    <h5 className="mb-0 text-green fw-semibold ms-3 fs-4">Sales Overview</h5>
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-0 px-5 pb-0">
                            {/* Summary-specific Loading Indicator */}
                            {loading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{minHeight: '200px'}}>
                                    <Spin tip="Loading Summary..." />
                                </div>
                            ) : (
                                <Carousel dots={false} arrows prevArrow={<CustomPrevArrow/>} nextArrow={<CustomNextArrow/>}>
                                    <div>
                                        <div className="border-till border rounded p-3 px-4 mb-3"
                                             style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                            <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Performance</h5>
                                        </div>
                                        <div className="carousel-content-scrollable"><Collapse items={performanceMetrics}/>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="border-till border rounded p-3 px-4 mb-3"
                                             style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                            <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Cost</h5>
                                        </div>
                                        <div className="carousel-content-scrollable"><Collapse items={costMetrics}/></div>
                                    </div>
                                    <div>
                                        <div className="border-till border rounded p-3 px-4 mb-3"
                                             style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                            <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Profit</h5>
                                        </div>
                                        <div className="carousel-content-scrollable"><Collapse items={profitLossMetrics}/>
                                        </div>
                                    </div>
                                </Carousel>
                            )}
                        </div>
                        <div className="card-footer p-3 px-5">
                            <div className="border border-till border-dashed rounded p-4 px-5 mb-3"
                                 style={{borderColor: '#63CE94 !important', background: '#63ce9440'}}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Tooltip
                                        title="Net Profit = Gross Profit - Expense + Net Tax"
                                        placement="top"
                                    >
                                        <h5 className="mb-0 text-green fw-semibold ms-3 fs-4" style={{cursor: 'help'}}>
                                            Net Profit
                                            <InfoCircleOutlined className="ms-2 text-gray-500"
                                                                style={{fontSize: '14px'}}/>
                                        </h5>
                                    </Tooltip>
                                    {loading ? <Spin size="small" /> : <h4 className="mb-0 fw-bolder text-green fs-3">{getAmount(tilesList?.net_profit || 0, selectedCurrencySign)}</h4>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ItemMaster
                selectedMarketplace={selectedMarketplace}
                selectedCurrencySign={selectedCurrencySign}
                userData={userData}
                selectedTileData={filters}
            />
        </Wrapper>
    );
};

export default ChartComponent;